import { useEffect, useState, useCallback } from "react";import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radio, Zap, CalendarClock, Star, Briefcase, Navigation, UserPlus, Power, Pencil, X, Clock, Crown } from "lucide-react";
import { api, apiError, formatDate, LEVELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/components/PlanGate";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INSTRUMENTS = ["Voz", "Guitarra", "Violão", "Cavaco", "Baixo", "Bateria", "Teclado", "Saxofone", "Trompete", "Violino", "Percussão", "Pandeiro", "DJ"];
const DURATIONS = [{ v: 60, l: "1 hora" }, { v: 120, l: "2 horas" }, { v: 180, l: "3 horas" }, { v: 240, l: "4 horas" }, { v: 360, l: "6 horas" }, { v: 480, l: "8 horas" }, { v: 720, l: "12 horas" }];
const inputCls = "mt-1.5 bg-[#121217] border-border rounded-sm";

const ONCALL_META = {
  ativo: { label: "🟢 Disponível agora", cls: "bg-emerald-500/15 text-success border-emerald-500/30" },
  agendado: { label: "🕐 Plantão programado", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  encerrado: { label: "Encerrado", cls: "bg-zinc-600/20 text-zinc-400 border-zinc-500/30" },
  cancelado: { label: "Cancelado", cls: "bg-zinc-600/20 text-zinc-400 border-zinc-500/30" },
  interrompido_conflito: { label: "Interrompido por conflito", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
};

function StatusBadge({ status }) {
  const m = ONCALL_META[status] || ONCALL_META.encerrado;
  return <span className={`text-[11px] px-2 py-0.5 rounded-sm border ${m.cls}`}>{m.label}</span>;
}

/* -------------------- Músico: Meu Plantão -------------------- */
function MyOnCall() {
  const [data, setData] = useState({ active: [], history: [], is_pro: false });
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // {mode, editing}
  const [form, setForm] = useState({ mode: "now", duration_min: 120, date: "", start_time: "", end_time: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/oncall/me"); setData(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const openNow = () => { setForm({ mode: "now", duration_min: 120, date: "", start_time: "", end_time: "", note: "" }); setDlg({ mode: "now" }); };
  const openScheduled = () => { setForm({ mode: "scheduled", duration_min: 120, date: "", start_time: "", end_time: "", note: "" }); setDlg({ mode: "scheduled" }); };
  const openEdit = (s) => {
    setForm({ mode: "scheduled", duration_min: 120, date: s.date, start_time: s.start_time, end_time: s.end_time, end_date: s.end_date, note: s.note || "" });
    setDlg({ mode: "scheduled", editing: s.id });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = form.mode === "now"
        ? { mode: "now", duration_min: Number(form.duration_min), note: form.note }
        : { mode: "scheduled", date: form.date, start_time: form.start_time, end_time: form.end_time, note: form.note };
      if (dlg.editing) await api.put(`/oncall/${dlg.editing}`, payload);
      else await api.post("/oncall", payload);
      toast.success(dlg.editing ? "Plantão atualizado!" : "Modo Plantão ativado!");
      setDlg(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const cancel = async (id) => {
    try { await api.post(`/oncall/${id}/cancel`); toast.success("Plantão desligado."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const isActive = data.active.length > 0;

  return (
    <div className="space-y-6" data-testid="my-oncall">
      {/* Status principal */}
      <div className="bg-[#121217] border border-border rounded-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className={`h-14 w-14 rounded-sm flex items-center justify-center shrink-0 ${isActive ? "bg-emerald-500/15" : "bg-[#1C1C24]"}`}>
          <Radio className={`h-7 w-7 ${isActive ? "text-success" : "text-zinc-500"}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-head text-xl font-bold">Modo Plantão</span>
            <span className={`text-xs font-semibold ${isActive ? "text-success" : "text-zinc-500"}`}>{isActive ? "🟢 Ativo" : "🔴 Inativo"}</span>
          </div>
          <p className="text-sm text-zinc-500 mt-0.5">Sinalize que você está disponível para oportunidades de última hora. A disponibilidade usa sua Agenda Inteligente.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="oncall-now-btn" onClick={openNow} className="bg-[#FF4D00] hover:bg-[#E64500] rounded-sm gap-2"><Zap className="h-4 w-4" />Disponível agora</Button>
          <Button data-testid="oncall-schedule-btn" onClick={openScheduled} variant="outline" className="rounded-sm bg-transparent border-border gap-2"><CalendarClock className="h-4 w-4" />Programar</Button>
        </div>
      </div>

      {loading ? <div className="text-zinc-500">Carregando...</div> : (
        <>
          {/* Plantões ativos */}
          <div>
            <h2 className="text-sm font-semibold text-zinc-300 mb-2">Plantões ativos</h2>
            {data.active.length === 0 ? (
              <div className="text-zinc-500 bg-[#121217] border border-dashed border-border rounded-sm p-8 text-center text-sm">Nenhum Plantão ativo. Ative para aparecer nas buscas de organizadores.</div>
            ) : (
              <div className="space-y-2">
                {data.active.map((s) => (
                  <div key={s.id} data-testid={`oncall-active-${s.id}`} className="bg-[#121217] border border-border rounded-sm p-4 flex flex-wrap items-center gap-3">
                    <StatusBadge status={s.status} />
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-medium text-sm">{formatDate(s.date)} · {s.start_time}–{s.end_time}{s.end_date !== s.date ? ` (+1d)` : ""}</div>
                      {s.note && <div className="text-xs text-zinc-500">{s.note}</div>}
                    </div>
                    {s.status === "agendado" && (
                      <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`oncall-edit-${s.id}`} className="rounded-sm bg-transparent border-border gap-1"><Pencil className="h-3.5 w-3.5" />Editar</Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid={`oncall-cancel-${s.id}`} className="rounded-sm bg-transparent border-red-500/30 text-destructive hover:bg-red-500/10 gap-1"><Power className="h-3.5 w-3.5" />Desligar</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#121217] border-border">
                        <AlertDialogHeader><AlertDialogTitle>Desligar Plantão?</AlertDialogTitle>
                          <AlertDialogDescription>Você será removido das buscas de Plantão e não receberá novos convites. Eventos já confirmados permanecem inalterados.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel className="bg-transparent border-border">Voltar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => cancel(s.id)} className="bg-red-600 hover:bg-red-700">Desligar</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Histórico */}
          {data.history.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-zinc-300 mb-2">Histórico</h2>
              <div className="space-y-2">
                {data.history.slice(0, 20).map((s) => (
                  <div key={s.id} className="bg-[#121217]/60 border border-border rounded-sm p-3 flex flex-wrap items-center gap-3 text-sm">
                    <StatusBadge status={s.status} />
                    <span className="text-zinc-400">{formatDate(s.date)} · {s.start_time}–{s.end_time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Dialog ativar/programar */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="bg-[#121217] border-border">
          <DialogHeader>
            <DialogTitle>{dlg?.editing ? "Editar Plantão" : dlg?.mode === "now" ? "Disponível agora" : "Programar Plantão"}</DialogTitle>
            <DialogDescription>Antes de ativar, sua Agenda Inteligente valida se você está livre (eventos confirmados e indisponibilidades bloqueiam).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {form.mode === "now" ? (
              <div>
                <Label className="text-zinc-400 text-xs">Ficar disponível por</Label>
                <Select value={String(form.duration_min)} onValueChange={(v) => setForm({ ...form, duration_min: Number(v) })}>
                  <SelectTrigger data-testid="oncall-duration" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121217] border-border">{DURATIONS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-zinc-400 text-xs">Data</Label><Input data-testid="oncall-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} /></div>
                <div><Label className="text-zinc-400 text-xs">Início</Label><Input data-testid="oncall-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={inputCls} /></div>
                <div><Label className="text-zinc-400 text-xs">Término</Label><Input data-testid="oncall-end" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={inputCls} /></div>
              </div>
            )}
            <div><Label className="text-zinc-400 text-xs">Observação (opcional)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Ex: cavaco e pandeiro, zona sul" className={inputCls} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)} className="rounded-sm bg-transparent border-border">Cancelar</Button>
            <Button data-testid="oncall-save-btn" onClick={save} disabled={saving} className="bg-[#FF4D00] hover:bg-[#E64500] rounded-sm">{saving ? "Salvando..." : dlg?.editing ? "Salvar" : "Ativar Plantão"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------- Organizador: Buscar músicos de Plantão -------------------- */
function FindOnCall() {
  const navigate = useNavigate();
  const [f, setF] = useState({ instrument: "", specialty: "", min_level: "", date: "", start_time: "", end_time: "", max_distance: "", min_stars: "" });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [myEvents, setMyEvents] = useState([]);
  const [invitee, setInvitee] = useState(null);

  const run = async () => {
    setLoading(true); setSearched(true);
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
    try { const { data } = await api.get("/oncall/search", { params }); setResults(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { api.get("/events?scope=mine").then((r) => setMyEvents(r.data.filter((e) => e.status === "aberto"))); }, []);

  const invite = async (eventId, roleId) => {
    try { await api.post(`/oncall/invite`, { event_id: eventId, user_id: invitee.id, role_id: roleId || null }); toast.success(`Convite de Plantão enviado a ${invitee.stage_name}!`); setInvitee(null); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="find-oncall">
      <div className="bg-[#121217] border border-border rounded-sm p-5 grid md:grid-cols-3 gap-4">
        <div><Label className="text-zinc-400 text-xs">Instrumento</Label>
          <Select value={f.instrument || "any"} onValueChange={(v) => setF({ ...f, instrument: v === "any" ? "" : v })}>
            <SelectTrigger data-testid="find-instrument" className="mt-1.5 bg-[#09090B] border-border rounded-sm"><SelectValue placeholder="Qualquer" /></SelectTrigger>
            <SelectContent className="bg-[#121217] border-border"><SelectItem value="any">Qualquer</SelectItem>{INSTRUMENTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-zinc-400 text-xs">Nível mínimo</Label>
          <Select value={f.min_level || "any"} onValueChange={(v) => setF({ ...f, min_level: v === "any" ? "" : v })}>
            <SelectTrigger className="mt-1.5 bg-[#09090B] border-border rounded-sm"><SelectValue placeholder="Qualquer" /></SelectTrigger>
            <SelectContent className="bg-[#121217] border-border"><SelectItem value="any">Qualquer</SelectItem>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-zinc-400 text-xs">Especialidade</Label><Input value={f.specialty} onChange={(e) => setF({ ...f, specialty: e.target.value })} placeholder="Ex: samba" className={inputCls} /></div>
        <div className="md:col-span-3 rounded-sm border border-border p-4 space-y-3">
          <div className="flex items-center gap-2 text-[#00F0FF] text-sm font-semibold"><CalendarClock className="h-4 w-4" />Quando você precisa? <span className="text-[11px] text-zinc-500 font-normal">(vazio = disponíveis agora)</span></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-zinc-400 text-xs">Data</Label><Input data-testid="find-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-zinc-400 text-xs">Início</Label><Input data-testid="find-start" type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-zinc-400 text-xs">Término</Label><Input data-testid="find-end" type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className={inputCls} /></div>
          </div>
        </div>
        <div><Label className="text-zinc-400 text-xs">Distância máx. (km)</Label><Input type="number" min="0" value={f.max_distance} onChange={(e) => setF({ ...f, max_distance: e.target.value })} className={inputCls} /></div>
        <div><Label className="text-zinc-400 text-xs">Estrelas mín.</Label><Input type="number" min="0" max="5" step="0.5" value={f.min_stars} onChange={(e) => setF({ ...f, min_stars: e.target.value })} className={inputCls} /></div>
        <div className="flex items-end"><Button data-testid="find-btn" onClick={run} className="bg-[#FF4D00] hover:bg-[#E64500] rounded-sm gap-2 w-full"><Radio className="h-4 w-4" />Buscar de Plantão</Button></div>
      </div>

      {loading ? <div className="text-zinc-500">Buscando...</div> : results.length === 0 ? (
        <div className="text-zinc-500 bg-[#121217] border border-dashed border-border rounded-sm p-12 text-center">{searched ? "Nenhum músico de Plantão compatível e disponível no momento." : "Faça uma busca para encontrar músicos de Plantão."}</div>
      ) : (
        <div className="space-y-2">
          {results.map((u) => (
            <div key={u.id} data-testid={`oncall-result-${u.id}`} className="flex flex-wrap items-center gap-4 bg-[#121217] border border-border rounded-sm p-4">
              <div onClick={() => navigate(`/musicos/${u.id}`)} className="h-12 w-12 rounded-sm bg-[#1C1C24] overflow-hidden flex items-center justify-center font-bold shrink-0 cursor-pointer">
                {u.photo ? <img src={u.photo} alt="" className="h-full w-full object-cover" /> : u.stage_name[0]}
              </div>
              <div className="flex-1 min-w-[160px] cursor-pointer" onClick={() => navigate(`/musicos/${u.id}`)}>
                <div className="font-semibold truncate flex items-center gap-2">{u.stage_name}{u.is_pro && <Crown className="h-3.5 w-3.5 text-amber-400" title="Pro" />}</div>
                <div className="text-sm text-zinc-500 truncate">{[u.city, u.state].filter(Boolean).join("/")} · {(u.instruments || []).slice(0, 3).join(", ")}</div>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <StatusBadge status={u.oncall_status} />
                <span className="flex items-center gap-1 text-zinc-400"><Clock className="h-3.5 w-3.5" />{u.available_from}–{u.available_to}</span>
                <span className="flex items-center gap-1 text-amber-400"><Star className="h-4 w-4 fill-amber-400" />{u.rating.avg || "—"}</span>
                <span className="flex items-center gap-1 text-zinc-400"><Briefcase className="h-4 w-4" />{u.gigs_done}</span>
                {u.distance_km != null && <span className="flex items-center gap-1 text-[#00F0FF] mono"><Navigation className="h-3.5 w-3.5" />~{u.distance_km}km</span>}
                <Button size="sm" data-testid={`oncall-invite-${u.id}`} onClick={() => setInvitee(u)} className="rounded-sm bg-[#FF4D00] hover:bg-[#E64500] gap-1"><UserPlus className="h-4 w-4" />Convidar para vaga</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!invitee} onOpenChange={(o) => !o && setInvitee(null)}>
        <DialogContent className="bg-[#121217] border-border">
          <DialogHeader><DialogTitle>Convidar {invitee?.stage_name}</DialogTitle><DialogDescription>Escolha um evento aberto e a vaga. O convite usa o fluxo normal de eventos e revalida a agenda no aceite.</DialogDescription></DialogHeader>
          {myEvents.length === 0 ? (
            <div className="text-sm text-zinc-400 py-4">Você não tem eventos abertos. <button onClick={() => navigate("/eventos/novo")} className="text-[#00F0FF] hover:underline">Criar um evento</button> primeiro.</div>
          ) : (
            <div className="space-y-3">
              {myEvents.map((e) => (
                <div key={e.id} className="bg-[#09090B] border border-border rounded-sm p-3">
                  <div className="font-medium">{e.name}</div>
                  <div className="text-sm text-zinc-500 mono mb-2">{formatDate(e.date)} · {e.start_time}</div>
                  <div className="flex flex-wrap gap-2">
                    {(e.roles || []).filter((r) => (r.open ?? r.quantity) > 0).map((r) => (
                      <button key={r.id} data-testid={`oncall-invite-role-${e.id}-${r.id}`} onClick={() => invite(e.id, r.id)}
                        className="text-xs bg-[#1C1C24] border border-border rounded-sm px-3 py-1.5 hover:border-[#FF4D00]/40 transition-colors">
                        {r.instrument}{r.cache != null ? ` · R$ ${r.cache}` : ""}
                      </button>
                    ))}
                    <button data-testid={`oncall-invite-auto-${e.id}`} onClick={() => invite(e.id, null)} className="text-xs bg-[#FF4D00]/10 text-[#FF4D00] border border-[#FF4D00]/30 rounded-sm px-3 py-1.5 hover:bg-[#FF4D00]/20 transition-colors">Vaga compatível</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function OnCall() {
  const { hasFeature } = useAuth();
  if (!hasFeature("oncall")) return <PlanGate feature="oncall" title="Modo Plantão" />;
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="oncall-page">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3"><Radio className="h-8 w-8 text-[#FF4D00]" />Modo Plantão</h1>
        <p className="text-zinc-500 mt-1">Disponibilidade imediata sobre a Agenda Inteligente — para oportunidades de última hora.</p>
      </div>
      <Tabs defaultValue="me">
        <TabsList className="bg-[#121217] border border-border">
          <TabsTrigger value="me" data-testid="tab-my-oncall">Meu Plantão</TabsTrigger>
          <TabsTrigger value="find" data-testid="tab-find-oncall">Buscar músicos de Plantão</TabsTrigger>
        </TabsList>
        <TabsContent value="me" className="mt-6"><MyOnCall /></TabsContent>
        <TabsContent value="find" className="mt-6"><FindOnCall /></TabsContent>
      </Tabs>
    </div>
  );
}
